import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

// ── Minimal orbit controls ────────────────────────────────────────────────────
function useOrbit(mountRef) {
  const orbit = useRef({ dragging: false, lastX: 0, lastY: 0, phi: 1.1, theta: 0.3, radius: 7 });

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;

    const onDown  = (e) => { orbit.current.dragging = true;  orbit.current.lastX = e.clientX; orbit.current.lastY = e.clientY; };
    const onUp    = ()  => { orbit.current.dragging = false; };
    const onMove  = (e) => {
      if (!orbit.current.dragging) return;
      orbit.current.theta -= (e.clientX - orbit.current.lastX) * 0.005;
      orbit.current.phi    = Math.max(0.05, Math.min(Math.PI - 0.05, orbit.current.phi + (e.clientY - orbit.current.lastY) * 0.005));
      orbit.current.lastX  = e.clientX;
      orbit.current.lastY  = e.clientY;
    };
    const onWheel = (e) => {
      e.preventDefault();
      orbit.current.radius = Math.max(1.5, Math.min(40, orbit.current.radius + e.deltaY * 0.015));
    };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup',   onUp);
    window.addEventListener('mousemove', onMove);
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup',   onUp);
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('wheel', onWheel);
    };
  }, [mountRef]);

  return orbit;
}

const StlViewerModal = ({ isOpen, onClose, stlFiles = [] }) => {
  const mountRef  = useRef(null);
  const rendRef   = useRef(null);
  const sceneRef  = useRef(null);
  const cameraRef = useRef(null);
  const meshRef   = useRef(null);
  const rafRef    = useRef(null);
  const centerRef = useRef(new THREE.Vector3());
  const orbit     = useOrbit(mountRef);

  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingMesh,  setLoadingMesh]  = useState(false);
  const [wireframe,    setWireframe]    = useState(false);
  const [autoRotate,   setAutoRotate]   = useState(true);
  const [meshColor,    setMeshColor]    = useState('#38bdf8');
  const [info,         setInfo]         = useState(null);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    if (isOpen && stlFiles.length > 0 && !selectedFile) setSelectedFile(stlFiles[0]);
    if (!isOpen) { setSelectedFile(null); setInfo(null); setError(null); }
  }, [isOpen, stlFiles]);

  // ── Init Three.js ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !mountRef.current) return;

    const container = mountRef.current;
    const W = container.clientWidth  || 600;
    const H = container.clientHeight || 400;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#0f172a');
    sceneRef.current = scene;

    scene.add(new THREE.GridHelper(20, 20, '#1e293b', '#1e293b'));
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));

    const dir = new THREE.DirectionalLight(0xffffff, 1);
    dir.position.set(5, 10, 7);
    dir.castShadow = true;
    scene.add(dir);

    const dir2 = new THREE.DirectionalLight(0x7dd3fc, 0.35);
    dir2.position.set(-5, -5, -5);
    scene.add(dir2);

    const pointLight = new THREE.PointLight(0x38bdf8, 1.2, 50);
    pointLight.position.set(0, 8, 0);
    scene.add(pointLight);

    const camera = new THREE.PerspectiveCamera(45, W / H, 0.001, 2000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(W, H);
    renderer.shadowMap.enabled = true;
    container.appendChild(renderer.domElement);
    rendRef.current = renderer;

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    let t = 0;
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      t += 0.007;
      if (autoRotateRef.current && meshRef.current) orbit.current.theta += 0.003;
      const { phi, theta, radius } = orbit.current;
      camera.position.set(
        centerRef.current.x + radius * Math.sin(phi) * Math.sin(theta),
        centerRef.current.y + radius * Math.cos(phi),
        centerRef.current.z + radius * Math.sin(phi) * Math.cos(theta),
      );
      camera.lookAt(centerRef.current);
      pointLight.position.set(Math.sin(t) * 8, 8, Math.cos(t) * 8);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      sceneRef.current = null; rendRef.current = null; cameraRef.current = null;
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoRotateRef = useRef(autoRotate);
  useEffect(() => { autoRotateRef.current = autoRotate; }, [autoRotate]);

  // ── Load STL via proxy Edge Function ─────────────────────────────────────
  useEffect(() => {
    if (!selectedFile || !sceneRef.current) return;

    const load = async () => {
      setLoadingMesh(true);
      setError(null);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Session expirée. Veuillez vous reconnecter.');

        const proxyUrl =
          `${supabase.supabaseUrl}/functions/v1/proxyStlFile` +
          `?file_path=${encodeURIComponent(selectedFile.filePath)}` +
          `&file_name=${encodeURIComponent(selectedFile.name)}`;

        const response = await fetch(proxyUrl, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `Erreur proxy: ${response.status}`);
        }

        const buffer = await response.arrayBuffer();
        if (buffer.byteLength === 0) throw new Error('Le fichier reçu est vide.');

        // ── Use THREE's built-in STLLoader (works with all modern versions) ──
        const loader   = new STLLoader();
        const geometry = loader.parse(buffer); // returns BufferGeometry directly
        geometry.computeVertexNormals();

        // Center + scale
        geometry.computeBoundingBox();
        const box    = geometry.boundingBox;
        const size   = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;

        geometry.translate(-center.x, -center.y + size.y * 0.5, -center.z);

        // Remove old mesh
        if (meshRef.current && sceneRef.current) {
          sceneRef.current.remove(meshRef.current);
          meshRef.current.geometry.dispose();
          meshRef.current.material.dispose();
          meshRef.current = null;
        }

        const material = new THREE.MeshPhongMaterial({
          color:     new THREE.Color(meshColor),
          specular:  new THREE.Color('#ffffff'),
          shininess: 70,
          wireframe,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.scale.setScalar(5 / maxDim);
        mesh.castShadow    = true;
        mesh.receiveShadow = true;
        sceneRef.current.add(mesh);
        meshRef.current = mesh;

        centerRef.current.set(0, 0, 0);
        orbit.current.radius = 7;
        orbit.current.phi    = 1.1;
        orbit.current.theta  = 0.3;

        setInfo({
          triangles: Math.round(geometry.attributes.position.count / 3).toLocaleString('fr-FR'),
          size:      selectedFile.size || `${(buffer.byteLength / 1024).toFixed(1)} Ko`,
          name:      selectedFile.name,
        });

      } catch (err) {
        console.error('[StlViewerModal] Error loading STL:', err);
        setError(err.message || 'Impossible de charger le fichier STL.');
      } finally {
        setLoadingMesh(false);
      }
    };

    load();
  }, [selectedFile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (meshRef.current) meshRef.current.material.wireframe = wireframe; }, [wireframe]);
  useEffect(() => { if (meshRef.current) meshRef.current.material.color.set(meshColor); },  [meshColor]);

  const stlList = stlFiles.filter(f =>
    f?.name?.toLowerCase().endsWith('.stl') || f?.fileType === 'stl'
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl bg-[#0f172a] rounded-2xl overflow-hidden shadow-2xl border border-slate-700/60 flex flex-col"
        style={{ height: '88vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/60 bg-[#0c1523]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center">
              <Icon name="Box" size={16} className="text-sky-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white leading-none">Aperçu STL</h2>
              {info && <p className="text-xs text-slate-400 mt-0.5">{info.name}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setWireframe(w => !w)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${wireframe ? 'border-sky-400 text-sky-400 bg-sky-400/10' : 'border-slate-600 text-slate-400 hover:border-slate-400'}`}
            >
              Filaire
            </button>
            <button
              onClick={() => setAutoRotate(r => !r)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${autoRotate ? 'border-sky-400 text-sky-400 bg-sky-400/10' : 'border-slate-600 text-slate-400 hover:border-slate-400'}`}
            >
              <Icon name="RotateCw" size={12} className="inline mr-1" />
              Rotation
            </button>
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-slate-600 text-slate-400 cursor-pointer hover:border-slate-400 transition-all">
              <Icon name="Palette" size={12} />
              <input
                type="color"
                value={meshColor}
                onChange={e => setMeshColor(e.target.value)}
                className="w-5 h-5 rounded cursor-pointer bg-transparent border-0 p-0"
              />
            </label>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {stlList.length > 1 && (
            <div className="w-52 flex-shrink-0 border-r border-slate-700/60 bg-[#0c1523] overflow-y-auto">
              <p className="text-xs text-slate-500 px-3 pt-3 pb-2 uppercase tracking-wider">Fichiers STL</p>
              {stlList.map(file => (
                <button
                  key={file.id}
                  onClick={() => setSelectedFile(file)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2.5 transition-all text-xs ${
                    selectedFile?.id === file.id
                      ? 'bg-sky-500/15 text-sky-300 border-r-2 border-sky-400'
                      : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
                  }`}
                >
                  <Icon name="Box" size={14} className="flex-shrink-0" />
                  <span className="truncate" title={file.technicalName || file.name}>
                    {file.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Viewport */}
          <div className="flex-1 relative" ref={mountRef} style={{ cursor: 'grab' }}>

            {loadingMesh && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#0f172a]/90">
                <div className="w-10 h-10 rounded-full border-2 border-slate-700 border-t-sky-400 animate-spin mb-3" />
                <p className="text-xs text-sky-400 tracking-widest uppercase animate-pulse">Chargement du mesh…</p>
              </div>
            )}

            {error && !loadingMesh && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#0f172a]/90 p-6">
                <Icon name="AlertTriangle" size={36} className="text-red-400 mb-3" />
                <p className="text-sm text-red-300 text-center max-w-sm">{error}</p>
                <button
                  onClick={() => setSelectedFile(f => ({ ...f }))}
                  className="mt-4 px-4 py-2 text-xs rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 transition-all"
                >
                  Réessayer
                </button>
              </div>
            )}

            {!selectedFile && !loadingMesh && !error && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
                <Icon name="Box" size={48} className="text-slate-700 mb-4" />
                <p className="text-sm text-slate-500">Aucun fichier STL disponible</p>
              </div>
            )}

            {info && !loadingMesh && (
              <div className="absolute bottom-3 left-3 flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 text-xs text-slate-400 pointer-events-none">
                <span>▲ {info.triangles} triangles</span>
                <span className="text-slate-600">|</span>
                <span>◉ {info.size}</span>
              </div>
            )}

            {info && !loadingMesh && (
              <div className="absolute top-3 left-3 text-xs text-slate-600 pointer-events-none">
                Clic + glisser pour tourner · Molette pour zoomer
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StlViewerModal;